import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import type React from 'react'
import type { PropsWithChildren } from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'

export type BannerVariant = 'neutral' | 'informative' | 'success' | 'warning' | 'error'

interface BannerProps {
  // Font Awesome icon name, e.g. 'circle-info', 'triangle-exclamation'
  icon: string
  variant?: BannerVariant
  title: string | React.ReactNode
  // FR-004: lighter background for less prominent banners
  subtle?: boolean
  className?: string
}

// FR-005: urgent tones are announced immediately by screen readers
const getRole = (variant: BannerVariant) =>
  variant === 'error' || variant === 'warning' ? 'alert' : 'status'

export default function Banner({
  icon,
  variant = 'neutral',
  title,
  subtle = false,
  className,
  children,
}: PropsWithChildren<BannerProps>) {
  const classes = useStyles()

  // FR-001: icon + title header, FR-002: variant drives colors, FR-003: children fill the body
  return (
    <div
      role={getRole(variant)}
      data-testid={`banner-${variant}`}
      className={clsx(
        classes.banner,
        classes[variant],
        { [classes[`${variant}Subtle`]]: subtle },
        className,
      )}
    >
      <div className={classes.header}>
        <FontAwesomeIcon
          name={icon}
          regular
          noBaseClass
          aria-hidden="true"
          className={classes.icon}
        />
        <Text variant="subtitle2" component="div" className={classes.title}>
          {title}
        </Text>
      </div>
      {children && <div className={classes.body}>{children}</div>}
    </div>
  )
}

const ICON_SIZE = 24
const HEADER_GAP = 16

const useStyles = makeStyles<Theme>((theme) => ({
  banner: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(),
    padding: theme.spacing(2),
    borderTop: '1px solid',
  },
  // Variant classes set the border, background and text color; icon and title inherit the color
  neutral: {
    color: theme.components.banner.neutral.text,
    borderTopColor: theme.components.banner.neutral.border,
    backgroundColor: theme.components.banner.neutral.background,
  },
  informative: {
    color: theme.components.banner.informative.text,
    borderTopColor: theme.components.banner.informative.border,
    backgroundColor: theme.components.banner.informative.background,
  },
  success: {
    color: theme.components.banner.success.text,
    borderTopColor: theme.components.banner.success.border,
    backgroundColor: theme.components.banner.success.background,
  },
  warning: {
    color: theme.components.banner.warning.text,
    borderTopColor: theme.components.banner.warning.border,
    backgroundColor: theme.components.banner.warning.background,
  },
  error: {
    color: theme.components.banner.error.text,
    borderTopColor: theme.components.banner.error.border,
    backgroundColor: theme.components.banner.error.background,
  },
  // FR-004: subtle classes override only the background of the matching variant
  neutralSubtle: {
    backgroundColor: theme.components.banner.neutral.subtleBackground,
  },
  informativeSubtle: {
    backgroundColor: theme.components.banner.informative.subtleBackground,
  },
  successSubtle: {
    backgroundColor: theme.components.banner.success.subtleBackground,
  },
  warningSubtle: {
    backgroundColor: theme.components.banner.warning.subtleBackground,
  },
  errorSubtle: {
    backgroundColor: theme.components.banner.error.subtleBackground,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: HEADER_GAP,
  },
  icon: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    color: 'inherit',
  },
  title: {
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
    color: 'inherit',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingLeft: ICON_SIZE + HEADER_GAP,
  },
}))
